import { useMemo } from 'react';
import { useUserRole } from './useUserRole';
import { categories } from '@/config/categories';
import { getAccessibleCategories, getAccessibleGroups, CategoryGroup } from '@/config/categoryTiers';

/**
 * Hook to get categories accessible to the current user based on their role
 */
export const useAccessibleCategories = () => {
  const { role, loading } = useUserRole();

  const accessibleCategories = useMemo(() => {
    if (loading) return [];
    
    const allowedCategoryIds = getAccessibleCategories(role);
    return categories.filter(category => allowedCategoryIds.includes(category.id));
  }, [role, loading]);

  const restrictedCategories = useMemo(() => {
    if (loading) return [];
    
    const allowedCategoryIds = getAccessibleCategories(role);
    return categories.filter(category => !allowedCategoryIds.includes(category.id));
  }, [role, loading]);

  const accessibleGroups = useMemo(() => {
    if (loading) return [];
    
    return getAccessibleGroups(role);
  }, [role, loading]);

  const groupedCategories = useMemo(() => {
    if (loading) return {};
    
    const grouped: Record<string, typeof categories> = {};
    
    accessibleGroups.forEach(group => {
      grouped[group.id] = categories.filter(category => 
        group.categories.includes(category.id)
      );
    });
    
    return grouped;
  }, [accessibleCategories, accessibleGroups, loading]);

  return {
    accessibleCategories,
    restrictedCategories,
    accessibleGroups,
    groupedCategories,
    loading,
    userRole: role
  };
};